import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, categories } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-foreground-500 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-50 mb-3">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">你访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line text-xs"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryObj = categories.find((c) => c.slug === article.category);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.publishDate,
          image: article.coverImage,
          url: `https://heiliaozx.com/article/${article.id}`,
          publisher: {
            '@type': 'Organization',
            name: '黑料在线看',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://heiliaozx.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.categoryName,
              item: `https://heiliaozx.com/category/${article.category}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <article>
              <header className="py-6 md:py-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="bg-primary-500/10 text-primary-400 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  {article.isHot && (
                    <span className="bg-accent-500/10 text-accent-400 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-fire-fill text-[10px]"></i>
                      </div>
                      HOT
                    </span>
                  )}
                  <span className="text-foreground-500 text-xs">{article.publishDate}</span>
                  <span className="text-foreground-500 text-xs">{article.readTime}分钟阅读</span>
                  <span className="text-foreground-500 text-xs">
                    {article.viewCount >= 10000
                      ? `${(article.viewCount / 10000).toFixed(1)}万次阅读`
                      : `${article.viewCount}次阅读`}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/20">
                    <i className="ri-user-line text-primary-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-foreground-200 text-sm font-medium">{article.author}</p>
                    <p className="text-foreground-500 text-xs">黑料在线看特约作者</p>
                  </div>
                </div>
              </header>

              {/* Cover Image */}
              <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 黑料在线看`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="article-content pb-10 border-b border-background-200/50"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex items-center gap-2 mt-6 flex-wrap">
                <span className="text-foreground-500 text-xs">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-background-100 text-foreground-400 text-xs px-3 py-1 rounded-full border border-background-200/50 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 mt-8 pb-10 border-b border-background-200/50">
                <span className="text-foreground-500 text-xs">分享：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500/20 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-400 text-sm"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500/20 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-400 text-sm"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500/20 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-foreground-400 text-sm"></i>
                </button>
              </div>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="py-10 md:py-14" aria-label="相关文章">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-article-line text-primary-400"></i>
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-50">
                    相关推荐
                  </h2>
                </div>
                <div className="space-y-3">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.id} article={related} variant="compact" />
                  ))}
                </div>
                <div className="text-center mt-6">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                  >
                    查看更多{article.categoryName}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}